import { useTranslation } from 'react-i18next';
import { formatMedcomDateTime } from '@/lib/medcom/parseCareCommunicationBundle';
import type { ThreadAttachment } from '@/lib/medcom/threadAttachments';

export function ThreadAttachmentPicker({
  attachments,
  selected,
  onToggle,
}: {
  attachments: readonly ThreadAttachment[];
  selected: ReadonlySet<string>;
  onToggle: (key: string) => void;
}) {
  const { t } = useTranslation();
  if (attachments.length === 0) return null;

  return (
    <div
      id="thread-attachment-picker"
      className="mt-4 rounded-md border border-ds-line bg-ds-sunk p-3"
    >
      <h4 className="text-sm font-semibold text-ds-text">
        {t('communicate.field_thread_attachments')}
      </h4>
      <p className="mt-1 text-xs text-ds-muted">
        {t('communicate.thread_attachments_hint')}
      </p>
      <ul className="mt-2 space-y-2">
        {attachments.map((attachment) => {
          const sent = formatMedcomDateTime(attachment.messageAt) ?? attachment.messageAt;
          return (
            <li key={attachment.key}>
              <label className="flex items-start gap-2 text-sm text-ds-text">
                <input
                  type="checkbox"
                  checked={selected.has(attachment.key)}
                  onChange={() => onToggle(attachment.key)}
                  className="mt-0.5 h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                />
                <span>
                  <span className="font-medium">
                    {attachment.title || t('communicate.attachment_label')}
                  </span>
                  {attachment.senderName || sent ? (
                    <span className="ml-1 text-xs text-ds-muted">
                      {attachment.senderName ? `${attachment.senderName}` : ''}
                      {attachment.senderName && sent ? ' · ' : ''}
                      {sent
                        ? t('communicate.thread_attachment_sent', { date: sent })
                        : null}
                    </span>
                  ) : null}
                  {attachment.identifier ? (
                    <span className="mt-0.5 block font-mono text-xs text-ds-muted">
                      {t('communicate.field_attachment_identifier')}: {attachment.identifier}
                    </span>
                  ) : null}
                  {(attachment.authorName || attachment.authorPhone) && (
                    <span className="mt-0.5 block text-xs text-ds-muted">
                      {t('communicate.field_attachment_author')}
                      {attachment.authorName ? `: ${attachment.authorName}` : ''}
                      {attachment.authorPhone ? ` · ${attachment.authorPhone}` : ''}
                    </span>
                  )}
                  {attachment.creation ? (
                    <span className="mt-0.5 block text-xs text-ds-muted">
                      {t('communicate.field_attachment_creation')}:{' '}
                      {formatMedcomDateTime(attachment.creation) ?? attachment.creation}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
